import { useState, useRef, useEffect } from "react";
import { ChatCircleDots, X, PaperPlaneRight, PawPrint } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";

function sessionId() {
  let s = localStorage.getItem("wiggles_concierge_sid");
  if (!s) { s = "sid_" + Math.random().toString(36).slice(2) + Date.now(); localStorage.setItem("wiggles_concierge_sid", s); }
  return s;
}

export default function Concierge() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Yo — I'm the Mr. Wigglez Concierge. Ask me about hoodies, tees, the Mrs. Wigglez line, matching human + dog sets, dog gear, city collabs or Neon Wrap." },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const send = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setLoading(true);
    try {
      const { data } = await api.post("/concierge/chat", { session_id: sessionId(), message: text });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (err) {
      setMessages((m) => [...m, { role: "assistant", content: formatApiError(err.response?.data?.detail) }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-6 right-6 z-50 h-14 w-14 rounded-full bg-primary text-primary-foreground grid place-items-center pulse-ring hover:scale-105 transition-transform"
        data-testid="concierge-fab"
        aria-label="Open concierge"
      >
        {open ? <X size={26} /> : <ChatCircleDots weight="fill" size={26} />}
      </button>

      {open && (
        <div className="fixed bottom-24 right-6 z-50 w-[92vw] max-w-sm h-[70vh] max-h-[560px] rounded-2xl overflow-hidden flex flex-col bg-popover border border-white/10 shadow-2xl" data-testid="concierge-panel">
          <div className="px-5 py-4 glass-header flex items-center gap-2">
            <PawPrint weight="fill" className="text-primary" size={18} />
            <div>
              <p className="font-modern font-semibold text-sm leading-none">Wigglez Concierge</p>
              <p className="text-[11px] text-muted-foreground mt-1">AI brand assistant</p>
            </div>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto no-scrollbar px-4 py-4 space-y-4">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`concierge-msg-${m.role}`}>
                <div className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                  m.role === "user" ? "bg-primary text-primary-foreground rounded-br-sm" : "bg-muted text-foreground rounded-bl-sm"
                }`}>
                  {m.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start" data-testid="concierge-typing">
                <div className="bg-muted px-4 py-3 rounded-2xl rounded-bl-sm flex gap-1">
                  <span className="h-2 w-2 rounded-full bg-primary/70 animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="h-2 w-2 rounded-full bg-primary/70 animate-bounce" style={{ animationDelay: "120ms" }} />
                  <span className="h-2 w-2 rounded-full bg-primary/70 animate-bounce" style={{ animationDelay: "240ms" }} />
                </div>
              </div>
            )}
          </div>

          <form onSubmit={send} className="p-3 border-t border-white/10 flex items-center gap-2">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask the concierge…"
              className="flex-1 bg-transparent border border-white/15 rounded-full px-4 py-2.5 text-sm outline-none focus:border-primary transition-colors"
              data-testid="concierge-input"
            />
            <button type="submit" disabled={loading} className="h-10 w-10 shrink-0 rounded-full bg-primary text-primary-foreground grid place-items-center disabled:opacity-50" data-testid="concierge-send">
              <PaperPlaneRight weight="fill" size={18} />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
